<script lang="ts">
  import { authActions } from '../stores/auth';
  import Button from './ui/button.svelte';
  import { Mail, Eye, EyeOff, Loader2, ChevronLeft, Globe } from 'lucide-svelte';
  import { _, locale } from 'svelte-i18n';
  import { setAppLocale, type AppLocale } from '../lib/i18n';

  let step = $state<'initial' | 'email-form' | 'register' | 'login' | 'forgot-password' | 'success'>('initial');
  let email = $state('');
  let password = $state('');
  let confirmPassword = $state('');
  let showPassword = $state(false);
  let showConfirmPassword = $state(false);
  let message = $state('');
  let error = $state('');
  let emailExists = $state(false);
  type LoadingAction = 'google' | 'email-continue' | 'register' | 'login' | 'forgot-password';
  let loadingAction = $state<LoadingAction | null>(null);

  // Language selector
  let showLanguageMenu = $state(false);
  const availableLanguages: { code: AppLocale; name: string; flag: string }[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  ];

  let currentLanguage = $derived(
    availableLanguages.find(l => l.code === $locale) || availableLanguages[0]
  );

  function selectLanguage(code: AppLocale) {
    setAppLocale(code);
    showLanguageMenu = false;
  }

  function beginLoading(action: LoadingAction) {
    loadingAction = action;
  }

  function endLoading() {
    loadingAction = null;
  }

  async function handleGoogleLogin() {
    error = '';
    beginLoading('google');

    try {
      const success = await authActions.signInWithGoogle();
      if (!success) {
        error = $_('login.errors.google_failed');
      }
    } finally {
      endLoading();
    }
  }

  function handleEmailContinue() {
    if (!email || !email.includes('@')) {
      error = $_('login.errors.invalid_email');
      return;
    }
    error = '';
    checkEmailAndProceed();
  }

  async function checkEmailAndProceed() {
    if (!email || !email.includes('@')) {
      error = $_('login.errors.invalid_email');
      return;
    }

    error = '';
    beginLoading('email-continue');

    try {
      const exists = await authActions.checkEmailExists(email);
      emailExists = exists;
      step = 'email-form';
    } catch (err) {
      console.error('Error checking email:', err);
      emailExists = false;
      step = 'email-form';
    } finally {
      endLoading();
    }
  }

  async function handleRegister() {
    if (!email || !password || !confirmPassword) {
      error = $_('login.errors.fill_all_fields');
      return;
    }

    if (password !== confirmPassword) {
      error = $_('login.errors.passwords_not_match');
      return;
    }

    if (password.length < 6) {
      error = $_('login.errors.password_too_short');
      return;
    }

    error = '';
    beginLoading('register');

    try {
      const result = await authActions.signUp(email, password);

      if (result.success) {
        if (result.needsConfirmation) {
          message = $_('login.success.activation_sent', { values: { email } });
          step = 'success';
        }
      } else {
        const lowerError = result.error?.toLowerCase() ?? '';
        if (
          lowerError.includes('already registered') ||
          lowerError.includes('already exists') ||
          lowerError.includes('failed to fetch')
        ) {
          error = $_('login.errors.account_exists');
        } else if (lowerError.includes('database error saving new user')) {
          error = $_('login.errors.database_error');
        } else {
          error = result.error || $_('login.errors.registration_failed');
        }
      }
    } finally {
      endLoading();
    }
  }

  async function handleLogin() {
    if (!email || !password) {
      error = $_('login.errors.fill_email_password');
      return;
    }

    error = '';
    beginLoading('login');

    try {
      const result = await authActions.signIn(email, password);

      if (!result.success) {
        if (result.error && (
          result.error.includes('Invalid login credentials') ||
          result.error.includes('User not found') ||
          result.error.includes('Invalid credentials')
        )) {
          error = $_('login.errors.invalid_credentials');
        } else {
          error = result.error || $_('login.errors.login_failed');
        }
      }
    } finally {
      endLoading();
    }
  }

  async function handleForgotPassword() {
    if (!email || !email.includes('@')) {
      error = $_('login.errors.invalid_email');
      return;
    }

    error = '';
    beginLoading('forgot-password');

    try {
      const success = await authActions.resetPassword(email);

      if (success) {
        message = $_('login.success.reset_sent', { values: { email } });
        step = 'success';
      } else {
        error = $_('login.errors.email_send_failed');
      }
    } finally {
      endLoading();
    }
  }

  function goBack() {
    error = '';
    message = '';
    if (step === 'email-form' || step === 'register' || step === 'login') {
      step = 'initial';
      email = '';
      password = '';
      confirmPassword = '';
    } else if (step === 'forgot-password') {
      step = 'login';
    }
  }

  function switchToRegister() {
    error = '';
    step = 'register';
    password = '';
    confirmPassword = '';
  }

  function switchToLogin() {
    error = '';
    step = 'login';
    password = '';
  }

  function goToForgotPassword() {
    error = '';
    step = 'forgot-password';
    password = '';
  }
</script>

<div class="min-h-screen bg-gradient-primary flex items-center justify-center px-4 relative">
  <!-- Language Selector - Fixed position -->
  <div class="fixed top-4 right-4 z-50">
    <div class="relative">
      <button
        type="button"
        onclick={() => showLanguageMenu = !showLanguageMenu}
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/90 hover:bg-white border border-gray-200 text-gray-700 text-sm font-medium transition-colors shadow-sm"
      >
        <Globe class="w-4 h-4" />
        <span>{currentLanguage.flag} {currentLanguage.name}</span>
      </button>

      {#if showLanguageMenu}
        <div class="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
          {#each availableLanguages as lang}
            <button
              type="button"
              onclick={() => selectLanguage(lang.code)}
              class="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 {$locale === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}"
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
              {#if $locale === lang.code}
                <span class="ml-auto text-blue-600">✓</span>
              {/if}
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  <div class="w-full max-w-md">
    <div class="p-8 bg-white backdrop-blur border-white/20 shadow-xl rounded-xl text-gray-900">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-2xl font-bold text-gray-900 mb-2">
          {#if step === 'initial'}
            {$_('login.title_login')}
          {:else if step === 'email-form'}
            {emailExists ? $_('login.title_login') : $_('login.title_register')}
          {:else if step === 'register'}
            {$_('login.title_register')}
          {:else if step === 'login'}
            {$_('login.title_login')}
          {:else if step === 'forgot-password'}
            {$_('login.title_forgot_password')}
          {:else if step === 'success'}
            {$_('login.title_success')}
          {/if}
        </h1>
        <p class="text-gray-600">
          {#if step === 'initial'}
            {$_('login.subtitle_initial')}
          {:else if step === 'email-form'}
            {emailExists ? $_('login.subtitle_enter_password') : $_('login.subtitle_create_password')}
          {:else if step === 'register'}
            {$_('login.subtitle_register')}
          {:else if step === 'login'}
            {$_('login.subtitle_login')}
          {:else if step === 'forgot-password'}
            {$_('login.subtitle_forgot_password')}
          {:else if step === 'success'}
            {$_('login.subtitle_success')}
          {/if}
        </p>
      </div>

      <!-- Error Message -->
      {#if error}
        <div class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-700 text-sm">{error}</p>
        </div>
      {/if}

      <!-- Success Message -->
      {#if message}
        <div class="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p class="text-green-700 text-sm">{message}</p>
        </div>
      {/if}

      <!-- Initial Screen -->
      {#if step === 'initial'}
        <div class="space-y-4">
          <Button
            onclick={() => handleGoogleLogin()}
            disabled={loadingAction === 'google'}
            class="w-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            {#if loadingAction === 'google'}
              <Loader2 class="mr-3 h-5 w-5 animate-spin" />
              {$_('login.google_logging_in')}
            {:else}
              <svg class="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {$_('login.google_login')}
            {/if}
          </Button>

          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">{$_('login.or')}</span>
            </div>
          </div>

          <div class="space-y-3">
            <div class="relative">
              <Mail class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                bind:value={email}
                placeholder={$_('login.email_placeholder')}
                class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                onkeydown={(e: KeyboardEvent) => e.key === 'Enter' && handleEmailContinue()}
              />
            </div>

            <Button
              onclick={handleEmailContinue}
              disabled={!email || loadingAction === 'email-continue'}
              class="w-full bg-gray-900 hover:bg-gray-800 text-white"
            >
              {#if loadingAction === 'email-continue'}
                <Loader2 class="mr-3 h-5 w-5 animate-spin" />
                {$_('login.email_verifying')}
              {:else}
                {$_('login.email_continue')}
              {/if}
            </Button>
          </div>
        </div>

      <!-- Email Form -->
      {:else if step === 'email-form'}
        <div class="space-y-4">
          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-blue-700 text-sm">Email: <strong>{email}</strong></p>
          </div>

          <div class="space-y-3">
            <div class="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                bind:value={password}
                placeholder={emailExists ? $_('login.password_placeholder') : $_('login.password_create_placeholder')}
                class="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                onkeydown={(e: KeyboardEvent) => e.key === 'Enter' && (emailExists ? handleLogin() : handleRegister())}
              />
              <button
                type="button"
                onclick={() => showPassword = !showPassword}
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {#if showPassword}
                  <EyeOff class="w-5 h-5" />
                {:else}
                  <Eye class="w-5 h-5" />
                {/if}
              </button>
            </div>

            {#if !emailExists}
              <div class="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  bind:value={confirmPassword}
                  placeholder={$_('login.password_confirm_placeholder')}
                  class="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                  onkeydown={(e: KeyboardEvent) => e.key === 'Enter' && handleRegister()}
                />
                <button
                  type="button"
                  onclick={() => showConfirmPassword = !showConfirmPassword}
                  class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {#if showConfirmPassword}
                    <EyeOff class="w-5 h-5" />
                  {:else}
                    <Eye class="w-5 h-5" />
                  {/if}
                </button>
              </div>
            {/if}

            <Button
              onclick={emailExists ? handleLogin : handleRegister}
              disabled={(loadingAction === 'login' || loadingAction === 'register') || !password || (!emailExists && !confirmPassword)}
              class={emailExists ? "w-full bg-blue-600 hover:bg-blue-700 text-white" : "w-full bg-red-600 hover:bg-red-700 text-white"}
            >
              {#if loadingAction === 'login'}
                <Loader2 class="mr-3 h-5 w-5 animate-spin" />
                {$_('login.logging_in')}
              {:else if loadingAction === 'register'}
                <Loader2 class="mr-3 h-5 w-5 animate-spin" />
                {$_('login.registering')}
              {:else}
                {emailExists ? $_('login.login_button') : $_('login.register_button')}
              {/if}
            </Button>
          </div>

          <!-- Switch between login/registration -->
          <div class="text-center border-t border-gray-200 pt-4">
            <p class="text-gray-600 text-sm mb-2">
              {emailExists ? $_('login.no_account') : $_('login.have_account')}
            </p>
            <Button
              onclick={() => { emailExists = !emailExists; password = ''; confirmPassword = ''; error = ''; }}
              variant="outline"
              class="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              {emailExists ? $_('login.register_button') : $_('login.login_button')}
            </Button>
          </div>

          {#if emailExists}
            <Button
              onclick={goToForgotPassword}
              variant="ghost"
              class="w-full text-gray-600"
            >
              {$_('login.forgot_password')}
            </Button>
          {/if}

          <div class="pt-2">
            <button type="button" class="btn btn-back" onclick={() => goBack()} aria-label={$_('buttons.back')}>
              <ChevronLeft class="w-4 h-4" />
            </button>
          </div>
        </div>

      <!-- Register Form -->
      {:else if step === 'register'}
        <div class="space-y-4">
          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-blue-700 text-sm">Email: <strong>{email}</strong></p>
          </div>

          <div class="space-y-3">
            <div class="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                bind:value={password}
                placeholder={$_('login.password_create_placeholder')}
                class="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
              />
              <button
                type="button"
                onclick={() => showPassword = !showPassword}
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {#if showPassword}
                  <EyeOff class="w-5 h-5" />
                {:else}
                  <Eye class="w-5 h-5" />
                {/if}
              </button>
            </div>

            <div class="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                bind:value={confirmPassword}
                placeholder={$_('login.password_confirm_placeholder')}
                class="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                onkeydown={(e: KeyboardEvent) => e.key === 'Enter' && handleRegister()}
              />
              <button
                type="button"
                onclick={() => showConfirmPassword = !showConfirmPassword}
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {#if showConfirmPassword}
                  <EyeOff class="w-5 h-5" />
                {:else}
                  <Eye class="w-5 h-5" />
                {/if}
              </button>
            </div>

            <Button
              onclick={() => handleRegister()}
              disabled={loadingAction === 'register' || !password || !confirmPassword}
              class="w-full bg-green-600 hover:bg-green-700"
            >
              {#if loadingAction === 'register'}
                <Loader2 class="mr-3 h-5 w-5 animate-spin" />
                {$_('login.registering')}
              {:else}
                {$_('login.register_button')}
              {/if}
            </Button>
          </div>

          <div class="space-y-2">
            <Button
              onclick={switchToLogin}
              variant="ghost"
              class="w-full text-blue-600"
            >
              {$_('login.have_account_login')}
            </Button>

          <div class="pt-2">
            <button type="button" class="btn btn-back" onclick={() => goBack()} aria-label={$_('buttons.back')}>
              <ChevronLeft class="w-4 h-4" />
            </button>
          </div>
          </div>
        </div>

      <!-- Login Form -->
      {:else if step === 'login'}
        <div class="space-y-4">
          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-blue-700 text-sm">Email: <strong>{email}</strong></p>
          </div>

          <div class="space-y-3">
            <div class="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                bind:value={password}
                placeholder={$_('login.password_placeholder')}
                class="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                onkeydown={(e: KeyboardEvent) => e.key === 'Enter' && handleLogin()}
              />
              <button
                type="button"
                onclick={() => showPassword = !showPassword}
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {#if showPassword}
                  <EyeOff class="w-5 h-5" />
                {:else}
                  <Eye class="w-5 h-5" />
                {/if}
              </button>
            </div>
          </div>
          <div class="space-y-3">
            <Button
              onclick={() => handleLogin()}
              disabled={loadingAction === 'login' || !password}
              class="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {#if loadingAction === 'login'}
                <Loader2 class="mr-3 h-5 w-5 animate-spin" />
                {$_('login.logging_in')}
              {:else}
                {$_('login.login_button')}
              {/if}
            </Button>
          </div>

          <div class="space-y-2">
            <Button
              onclick={goToForgotPassword}
              variant="ghost"
              class="w-full text-gray-600"
            >
              {$_('login.forgot_password')}
            </Button>

            <Button
              onclick={switchToRegister}
              variant="ghost"
              class="w-full text-blue-600"
            >
              {$_('login.create_account')}
            </Button>

          <div class="pt-2">
            <button type="button" class="btn btn-back" onclick={() => goBack()} aria-label={$_('buttons.back')}>
              <ChevronLeft class="w-4 h-4" />
            </button>
          </div>
          </div>
        </div>

      <!-- Forgot Password Form -->
      {:else if step === 'forgot-password'}
        <div class="space-y-4">
          <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p class="text-yellow-700 text-sm">{$_('login.forgot_password_info')}</p>
          </div>

          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-blue-700 text-sm">Email: <strong>{email}</strong></p>
          </div>

          <Button
            onclick={() => handleForgotPassword()}
            disabled={loadingAction === 'forgot-password'}
            class="w-full bg-orange-600 hover:bg-orange-700 text-white"
          >
            {#if loadingAction === 'forgot-password'}
              <Loader2 class="mr-3 h-5 w-5 animate-spin" />
              {$_('login.sending')}
            {:else}
              {$_('login.send_email')}
            {/if}
          </Button>

          <div class="pt-2">
            <button type="button" class="btn btn-back" onclick={() => goBack()}>
              <ChevronLeft class="w-4 h-4" />
              {$_('buttons.back_to_login')}
            </button>
          </div>
        </div>

      <!-- Success Screen -->
      {:else if step === 'success'}
        <div class="space-y-6 py-4">
          <div class="text-center">
            <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <button
                type="button"
                onclick={() => step = 'initial'}
                class="btn btn-back"
                aria-label={$_('buttons.back_to_login')}
              >
                <ChevronLeft class="w-4 h-4" />
              </button>
            </div>

            <button
              onclick={() => { step = 'initial'; email = ''; password = ''; confirmPassword = ''; message = ''; error = ''; }}
              class="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50 py-3 px-4 rounded-lg font-medium transition-colors border border-gray-200"
            >
              {$_('login.try_different_email')}
            </button>
          </div>
        </div>
      {/if}

      <!-- Privacy Policy Link -->
      {#if step === 'initial'}
        <div class="mt-6 text-center">
          <p class="text-xs text-gray-500">
            {$_('login.privacy_policy')}
            <a href="/privacy-policy" class="text-blue-600 hover:underline">
              {$_('login.privacy_policy_link')}
            </a>
          </p>
        </div>
      {/if}
    </div>
  </div>
</div>
